import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Palette, Check, Lock } from "lucide-react";
import useFeatureAccess from "@/hooks/useFeatureAccess";

export const ThemeManagement = () => {
  const { theme, availableThemes, updateTheme, refreshTheme } = useTheme();
  const { canSaveFeature, loading: accessLoading } = useFeatureAccess();
  const [saving, setSaving] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(theme);
  
  const { canSave, reason } = canSaveFeature("themes");

  useEffect(() => {
    setSelectedTheme(theme);
  }, [theme]);

  const themeDetails = {
    rapido: {
      name: "Rapido",
      description: "Yellow & Black theme inspired by Rapido's energetic branding",
      colors: { primary: "#FFCE00", secondary: "#1A1A1A", accent: "#FFE566" },
      logo: "🏍️"
    },
    uber: {
      name: "Uber",
      description: "Clean Black & White theme with blue accents, Uber-style",
      colors: { primary: "#000000", secondary: "#545454", accent: "#276EF1" },
      logo: "🚗"
    },
    ola: {
      name: "Ola",
      description: "Fresh Green theme reflecting Ola's eco-friendly image",
      colors: { primary: "#1C8B3C", secondary: "#2E7D32", accent: "#4CAF50" },
      logo: "🛺"
    }
  };

  const handleSaveTheme = async () => {
    if (selectedTheme === theme) {
      toast.info("Theme is already active");
      return;
    }

    setSaving(true);
    try {
      const success = await updateTheme(selectedTheme);
      if (success) {
        toast.success(`Theme changed to ${themeDetails[selectedTheme]?.name || selectedTheme}`);
      } else {
        toast.error("Failed to update theme");
      }
    } catch (err) {
      toast.error("Failed to update theme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="theme-management">
      <h1 className="text-2xl font-bold text-foreground mb-2">Theme Management</h1>
      <p className="text-muted-foreground mb-6">Customize your app's look and feel. Changes apply instantly across all users.</p>

      {/* Restriction Banner */}
      {!canSave && reason && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">{reason}</p>
            <p className="text-sm text-amber-600">You can preview themes but cannot apply changes.</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {Object.entries(themeDetails).map(([key, details]) => (
          <button
            key={key}
            onClick={() => setSelectedTheme(key)}
            className={`relative p-6 rounded-2xl border-2 transition-all text-left ${
              selectedTheme === key
                ? "border-primary bg-primary/5 shadow-lg"
                : "border-border hover:border-primary/30"
            }`}
            data-testid={`theme-${key}`}
          >
            {theme === key && (
              <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                <Check className="w-4 h-4 text-primary-foreground" />
              </div>
            )}
            
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl"
                style={{ backgroundColor: details.colors.primary }}
              >
                <span style={{ filter: key === 'uber' ? 'invert(1)' : 'none' }}>
                  {details.logo}
                </span>
              </div>
              <div>
                <h3 className="font-bold text-lg text-foreground">{details.name}</h3>
                <p className="text-xs text-muted-foreground">Theme</p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-4">{details.description}</p>

            <div className="flex gap-2">
              <div 
                className="w-8 h-8 rounded-lg border border-border/50" 
                style={{ backgroundColor: details.colors.primary }}
                title="Primary"
              />
              <div 
                className="w-8 h-8 rounded-lg border border-border/50" 
                style={{ backgroundColor: details.colors.secondary }}
                title="Secondary"
              />
              <div 
                className="w-8 h-8 rounded-lg border border-border/50" 
                style={{ backgroundColor: details.colors.accent }}
                title="Accent"
              />
            </div>

            {selectedTheme === key && selectedTheme !== theme && (
              <div className="mt-4 pt-4 border-t border-border/50">
                <span className="text-xs text-primary font-medium">Selected - Click Apply to save</span>
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-4">
        <Button
          size="lg"
          className="px-8"
          onClick={() => {
            if (!canSave) {
              toast.error(reason || "This feature is not available");
              return;
            }
            handleSaveTheme();
          }}
          disabled={saving || selectedTheme === theme || !canSave}
          data-testid="apply-theme-btn"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
          ) : (
            <>
              {!canSave ? <Lock className="w-5 h-5 mr-2" /> : <Palette className="w-5 h-5 mr-2" />}
              Apply Theme
            </>
          )}
        </Button>
        {selectedTheme !== theme && (
          <Button
            variant="outline"
            onClick={() => setSelectedTheme(theme)}
            data-testid="cancel-theme-btn"
          >
            Cancel
          </Button>
        )}
      </div>

      <div className="mt-8 p-6 bg-card rounded-xl border border-border">
        <h3 className="font-semibold text-foreground mb-4">Live Preview</h3>
        <p className="text-sm text-muted-foreground mb-4">See how the selected theme looks on UI elements</p>
        
        <div className="flex flex-wrap gap-4">
          <Button>Primary Button</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="destructive">Destructive</Button>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-primary rounded-lg">
            <span className="text-primary-foreground text-sm font-medium">Primary</span>
          </div>
          <div className="p-4 bg-secondary rounded-lg">
            <span className="text-secondary-foreground text-sm font-medium">Secondary</span>
          </div>
          <div className="p-4 bg-accent rounded-lg">
            <span className="text-accent-foreground text-sm font-medium">Accent</span>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <span className="text-muted-foreground text-sm font-medium">Muted</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeManagement;
